import { useEffect, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react'
import { Icon } from './Icon'

/** Bloque shimmer para estados de carga. Usa .skel de platform.css. */
export function Skeleton({ width = '100%', height = 16, radius, style }: { width?: number | string; height?: number | string; radius?: number; style?: CSSProperties }) {
  return <span className="skel" style={{ display: 'block', width, height, borderRadius: radius, ...style }} aria-hidden />
}

export function TableSkeleton({ columns = 5, rows = 6 }: { columns?: number; rows?: number }) {
  return (
    <table className="table" aria-hidden>
      <thead>
        <tr>
          {Array.from({ length: columns }).map((_, index) => (
            <th key={index}><Skeleton width={index === 0 ? '48%' : '62%'} height={12} /></th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <tr key={rowIndex}>
            {Array.from({ length: columns }).map((_, colIndex) => (
              <td key={colIndex}><Skeleton width={colIndex === 0 ? '72%' : colIndex % 2 === 0 ? '54%' : '38%'} height={14} /></td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function CardGridSkeleton({ cards = 3, minHeight = 160, columns = 3 }: { cards?: number; minHeight?: number; columns?: number }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 14 }} aria-hidden>
      {Array.from({ length: cards }).map((_, index) => (
        <div key={index} className="card">
          <div style={{ padding: 18, minHeight }}>
            <Skeleton width="42%" height={20} style={{ marginBottom: 14 }} />
            <Skeleton width="74%" height={32} style={{ marginBottom: 14 }} />
            <Skeleton width="100%" height={12} style={{ marginBottom: 8 }} />
            <Skeleton width="68%" height={12} />
          </div>
        </div>
      ))}
    </div>
  )
}

/** Hace que una fila/tarjeta clickeable sea operable por teclado (Enter/Espacio)
 *  sin pisar la semántica de tabla. Se combina con el onClick existente para el mouse. */
export function rowActivation(onActivate: () => void) {
  return {
    tabIndex: 0,
    onKeyDown: (e: ReactKeyboardEvent<HTMLElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onActivate()
      }
    },
  }
}

const STATUS_TONES: Record<string, string> = {
  active: 'success',
  trialing: 'info',
  past_due: 'warning',
  cancelled: 'neutral',
  suspended: 'danger',
  requested: 'info',
  provisioning: 'warning',
  failed: 'danger',
  ok: 'success',
  degraded: 'warning',
  down: 'danger',
  unknown: 'neutral',
  success: 'success',
  error: 'danger',
  running: 'warning',
  lead: 'info',
  trial: 'info',
  paused: 'warning',
  churned: 'neutral',
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Activo',
  trialing: 'En trial',
  past_due: 'Past due',
  cancelled: 'Cancelado',
  suspended: 'Suspendido',
  requested: 'Solicitado',
  provisioning: 'Provisionando',
  failed: 'Fallo',
  ok: 'OK',
  degraded: 'Degradado',
  down: 'Caido',
  unknown: 'Sin datos',
  success: 'OK',
  error: 'Error',
  running: 'Corriendo',
  lead: 'Lead',
  trial: 'Trial',
  paused: 'Pausado',
  churned: 'Churned',
}

export function Badge({ tone = 'neutral', dot, size, children }: { tone?: string; dot?: boolean; size?: 'sm'; children: ReactNode }) {
  return (
    <span className={`badge badge-${tone} ${size === 'sm' ? 'badge-sm' : ''}`}>
      {dot ? <span className="dot" /> : null}
      {children}
    </span>
  )
}

export function StatusBadge({ status, size }: { status?: string | null; size?: 'sm' }) {
  const key = status || 'unknown'
  return <Badge tone={STATUS_TONES[key] || 'neutral'} dot size={size}>{STATUS_LABELS[key] || key}</Badge>
}

export function HealthDot({ status }: { status?: string | null }) {
  const key = status || 'unknown'
  const labels: Record<string, string> = { ok: 'Operativo', degraded: 'Degradado', down: 'Caido', unknown: 'Sin datos' }
  return <span className={`health-dot health-${key}`}>{labels[key] || key}</span>
}

export function PlanBadge({ plan }: { plan?: string | null }) {
  const key = plan || 'trial'
  const label = key.charAt(0).toUpperCase() + key.slice(1)
  return <span className={`plan-badge ${key}`}>{label}</span>
}

export function Avatar({ initials = 'BI', size = 'md' }: { initials?: string; size?: 'sm' | 'md' }) {
  return <span className={`avatar ${size === 'sm' ? 'sm' : ''}`} style={{ background: '#15B868', color: '#0F1C14' }}>{initials}</span>
}

export function AppButton({ variant = 'secondary', size, icon, children, onClick, type = 'button', disabled }: { variant?: string; size?: 'sm' | 'lg'; icon?: string; children: ReactNode; onClick?: () => void; type?: 'button' | 'submit'; disabled?: boolean }) {
  return <button type={type} className={`btn ${variant} ${size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : ''}`} onClick={onClick} disabled={disabled}>{icon ? <Icon name={icon} size={14} /> : null}{children}</button>
}

export function FilterChip({ active, onClick, label, count }: { active?: boolean; onClick?: () => void; label: string; count?: number }) {
  return <button type="button" className={`filter-chip ${active ? 'active' : ''}`} onClick={onClick}>{label}{typeof count === 'number' ? <span className="count">{count}</span> : null}</button>
}

export function Sparkline({ data, color = '#15B868', width = 80, height = 30 }: { data: number[]; color?: string; width?: number; height?: number }) {
  if (data.length < 2) return null
  const min = Math.min(...data)
  const max = Math.max(...data)
  const range = max - min || 1
  const step = width / (data.length - 1)
  const points = data.map((value, index) => `${index * step},${height - ((value - min) / range) * (height - 4) - 2}`).join(' ')
  const area = `M 0,${height} L ${points.replaceAll(' ', ' L ')} L ${width},${height} Z`

  return <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="kpi-spark"><path d={area} fill={color} opacity="0.12" /><polyline points={points} fill="none" stroke={color} strokeWidth="1.5" /></svg>
}

export function KPICard({ label, value, unit, trend, trendDir = 'up', spark, feature, icon, loading }: { label: string; value: ReactNode; unit?: string; trend?: string; trendDir?: 'up' | 'down'; spark?: number[]; feature?: boolean; icon?: string; loading?: boolean }) {
  return (
    <div className={`kpi-card ${feature ? 'feature' : ''}`}>
      <div className="kpi-label">{icon ? <Icon name={icon} size={14} /> : null}{label}</div>
      {loading
        ? <div className="kpi-value"><Skeleton width={90} height={26} /></div>
        : <div className="kpi-value">{value}{unit ? <span className="unit">{unit}</span> : null}</div>}
      {!loading && trend ? <div className={`kpi-trend ${trendDir}`}><Icon name={trendDir === 'up' ? 'arrowUp' : 'arrowDown'} size={12} />{trend}</div> : null}
      {!loading && spark ? <Sparkline data={spark} /> : null}
    </div>
  )
}

export function EmptyState({ icon = 'inbox', title, desc }: { icon?: string; title: string; desc?: string }) {
  return <div className="empty-state"><div className="emoji"><Icon name={icon} size={24} /></div><h3>{title}</h3>{desc ? <p>{desc}</p> : null}</div>
}

export function formatMoney(amount: number) {
  return '$' + amount.toLocaleString('es-AR', { maximumFractionDigits: 0 })
}

export function formatCount(value: number) {
  if (value >= 1000) return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}K`
  return String(value)
}

export function scoreTier(score: number): { color: 'cold' | 'warm' | 'hot' | 'fire'; label: string } {
  if (score >= 81) return { color: 'fire', label: 'En llamas' }
  if (score >= 51) return { color: 'hot', label: 'Caliente' }
  if (score >= 21) return { color: 'warm', label: 'Tibio' }
  return { color: 'cold', label: 'Frío' }
}

export function ScoreChip({ score, showTier = true }: { score: number; showTier?: boolean }) {
  const tier = scoreTier(score)
  const fillPct = `${Math.min(100, Math.max(8, score))}%`
  return (
    <span className={`score-chip ${tier.color}`} style={{ ['--therm-fill' as string]: fillPct }}>
      <span className="therm" aria-hidden />
      <span>{score}</span>
      {showTier ? <span className="tier">{tier.color === 'fire' ? '🔥' : tier.label.slice(0, 4).toLowerCase()}</span> : null}
    </span>
  )
}

export function IconButton({ icon, onClick, tooltip, size = 16 }: { icon: string; onClick?: () => void; tooltip?: string; size?: number }) {
  return (
    <span className="tooltip-host" data-tooltip={tooltip}>
      <button className="btn-icon" onClick={onClick} type="button" aria-label={tooltip}>
        <Icon name={icon} size={size} />
      </button>
    </span>
  )
}

export interface TabItem {
  id: string
  label: string
  icon?: string
  count?: number
}

export function Tabs({ tabs, active, onChange }: { tabs: TabItem[]; active: string; onChange: (id: string) => void }) {
  return (
    <div className="tabs">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          className={`tab ${active === tab.id ? 'active' : ''}`}
          onClick={() => onChange(tab.id)}
          type="button"
        >
          {tab.icon ? <Icon name={tab.icon} size={14} /> : null}
          {tab.label}
          {typeof tab.count === 'number' ? <span className="tab-count">{tab.count}</span> : null}
        </button>
      ))}
    </div>
  )
}

export function Modal({ open, onClose, title, subtitle, children, footer, size = 'md' }: {
  open: boolean
  onClose?: () => void
  title?: ReactNode
  subtitle?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  size?: 'md' | 'lg'
}) {
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && onClose) onClose() }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])
  if (!open) return null
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className={`modal ${size === 'lg' ? 'lg' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        onClick={(e) => e.stopPropagation()}
        style={{ overscrollBehavior: 'contain' }}
      >
        <div className="modal-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2>{title}</h2>
              {subtitle ? <div className="subtitle">{subtitle}</div> : null}
            </div>
            <IconButton icon="x" onClick={onClose} tooltip="Cerrar" />
          </div>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-footer">{footer}</div> : null}
      </div>
    </div>
  )
}

export function Drawer({ open, onClose, title, children }: {
  open: boolean
  onClose?: () => void
  title?: ReactNode
  children?: ReactNode
}) {
  useEffect(() => {
    if (!open) return undefined
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && onClose) onClose() }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])
  if (!open) return null
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        style={{ overscrollBehavior: 'contain' }}
      >
        <div className="drawer-header">
          <h2 className="t-h2">{title}</h2>
          <IconButton icon="x" onClick={onClose} tooltip="Cerrar" />
        </div>
        <div className="drawer-body">{children}</div>
      </div>
    </>
  )
}
